import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  changeMyPassword,
  isMyAccountTab,
  myAccountShellQuery,
  myEventsQuery,
  myPrivacyQuery,
  myProfileQuery,
  removeMyAvatar,
  saveMyAvatar,
  saveMyPrivacy,
  savePersonalInfo,
  type MyProfile,
} from '../../api/settingsMyAccount'
import { DataTable, useTableState } from '../../ui/DataTable'
import { FormActions, FormRow, HalfField } from '../../ui/FormGrid'
import { Forbidden } from '../../ui/Forbidden'
import { InnerTabs } from '../../ui/InnerTabs'
import { Modal } from '../../ui/Modal'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { eventColumns } from '../users/AccountWorkspace'
import { failed, tableRequest } from '../users/shared'
import { OutOfOfficePanel } from './OutOfOfficePanel'

/**
 * Settings -> My Account: bara cu doua intrari si cele doua ecrane ale ei.
 *
 * Perechea veche e `views/version-3/settings/user_settings/index.php` (bara,
 * cu capul "User") plus `my_account/index.php` si `out_of_office/index.php`.
 *
 * Calea e chiar cea a portalului - `/app/settings/user_accounts` - fiindca ea e
 * scrisa in rotita din antet, la randul "My Account" (vezi `SettingsDrawer`).
 *
 * Bara e desenata orizontal, ca la celelalte ecrane mutate: coloana din stanga
 * e acum a rail-ului de zona.
 */
export function MyAccountPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(myAccountShellQuery)

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isMyAccountTab(tab)) return
    void navigate({ to: '/settings/user_accounts/$tab', params: { tab: 'my_account' } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const current = isMyAccountTab(tab) ? tab : 'my_account'

  return (
    <>
      <nav className="od-tabbar">
        <ul className="od-tabbar-nav">
          <li className="menu-header">User</li>
          {shell.data.tabs.map((item) => (
            <TabLink key={item.id} id={item.id} label={item.label} icon={item.icon} current={current} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </nav>

      <div className="page-content-wrapper">
        {current === 'my_account' && <MyAccountPanel tabs={shell.data.accountTabs} />}
        {current === 'out_of_office' && <OutOfOfficePanel />}
      </div>
    </>
  )
}

function TabLink({
  id,
  label,
  icon,
  current,
}: {
  id: string
  label: string
  icon: string
  current: string
}) {
  const navigate = useNavigate()
  const go = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    void navigate({ to: '/settings/user_accounts/$tab', params: { tab: id } })
  }
  return (
    <li className={id === current ? 'menu-tab active' : 'menu-tab'}>
      <a id={id} href={'/app/settings/user_accounts/' + id} onClick={go}>
        <i className={icon} aria-hidden="true"></i> {label}
      </a>
    </li>
  )
}

/* =======================================================================
 * My Account: panoul cu chipul, si cinci file langa el
 * ==================================================================== */

function MyAccountPanel({ tabs }: { tabs: { id: string; label: string }[] }) {
  const page = useQuery(myProfileQuery)
  const [current, setCurrent] = useState('personal_info')

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  return (
    <div id="content">
      <div className="row">
        <div className="col-md-12">
          <div
            className="profile-sidebar bg-white box-shadow"
            style={{ float: 'left', width: 300, marginRight: 20 }}
          >
            <ProfileSidebar profile={page.data} />
          </div>

          <div className="profile-content box-shadow" style={{ overflow: 'hidden' }}>
            <div className="row">
              <div className="col-md-12">
                {/* Filele dinauntru, ca bara de deasupra lor: vezi `ui/InnerTabs`. */}
                <InnerTabs tabs={tabs} current={current} onPick={setCurrent} className="userTabs" />
                <div className="tab-content p-3 m-0 overflow-hidden">
                  {current === 'personal_info' && <PersonalInfoForm profile={page.data} />}
                  {current === 'personal_avatar' && <AvatarForm profile={page.data} />}
                  {current === 'change_password' && <PasswordForm hint={page.data.passwordHint} />}
                  {current === 'privacy_settings' && <PrivacyForm />}
                  {current === 'account_events' && <MyEvents />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** `my_account/profile_sidebar.php`. */
function ProfileSidebar({ profile }: { profile: MyProfile }) {
  const user = profile.user
  return (
    <div className="profile-sidebar-portlet" style={{ padding: '30px 0' }}>
      <div className="profile-useravatar text-center">
        {user.avatarUrl !== '' ? (
          <img
            src={user.avatarUrl}
            alt=""
            onError={(event) => {
              /* `onerror="this.style.display='none'"` din macheta veche. */
              event.currentTarget.style.display = 'none'
            }}
          />
        ) : null}
      </div>

      <div className="profile-usertitle" style={{ textAlign: 'center' }}>
        <div className="profile-usertitle-name mt-2">
          <h3>
            {user.firstname} {user.lastname}
          </h3>
        </div>

        <div className="profile-usertitle-job mt-1 font-12">{user.occupation}</div>

        {user.email !== '' ? (
          <div className="profile-usertitle-others mt-1 font-12">
            <i className="far fa-envelope" aria-hidden="true"></i> {user.email}
          </div>
        ) : null}

        {user.mobileNumber !== '' ? (
          <div className="profile-usertitle-others mt-1 font-12">
            <i className="fa fa-phone" aria-hidden="true"></i> {user.mobileNumber}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** `my_account/personal_info.php`. */
function PersonalInfoForm({ profile }: { profile: MyProfile }) {
  const client = useQueryClient()
  const [form, setForm] = useState(profile.user)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [signature, setSignature] = useState(false)

  useEffect(() => {
    setForm(profile.user)
  }, [profile.user])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setErrors({})
    void savePersonalInfo({
      firstname: form.firstname,
      lastname: form.lastname,
      mobile_number: form.mobileNumber,
      work_number: form.workNumber,
      email: form.email,
      company: form.companyId,
      occupation: form.occupation,
      address: form.address,
    })
      .then(() => {
        toast('Changes saved', 'Success')
        void client.invalidateQueries({ queryKey: ['settings', 'my_account'] })
      })
      .catch((error: unknown) => {
        const fields = error instanceof ApiError ? (error.details?.fields as Record<string, string> | undefined) : undefined
        setErrors(fields ?? {})
        toast(failed(error), 'Error', 'error')
      })
      .finally(() => setBusy(false))
  }

  const hint = (name: string) =>
    errors[name] ? <span className="help-block text-danger">{errors[name]}</span> : null

  return (
    <>
      <form role="form" id="personal_info_form" method="post" onSubmit={submit}>
        <FormRow>
          <HalfField label="First Name" required htmlFor="my_firstname">
            <input
              id="my_firstname"
              className="form-control"
              type="text"
              required
              value={form.firstname}
              onChange={(e) => setForm({ ...form, firstname: e.target.value })}
            />
            {hint('firstname')}
          </HalfField>
          <HalfField label="Last Name" required htmlFor="my_lastname">
            <input
              id="my_lastname"
              className="form-control"
              type="text"
              required
              value={form.lastname}
              onChange={(e) => setForm({ ...form, lastname: e.target.value })}
            />
            {hint('lastname')}
          </HalfField>
        </FormRow>

        <FormRow>
          <HalfField label="Mobile Number" htmlFor="my_mobile_number">
            <input
              id="my_mobile_number"
              className="form-control"
              type="text"
              value={form.mobileNumber}
              onChange={(e) => setForm({ ...form, mobileNumber: e.target.value })}
            />
          </HalfField>
          <HalfField label="Work Number" htmlFor="my_work_number">
            <input
              id="my_work_number"
              className="form-control"
              type="text"
              value={form.workNumber}
              onChange={(e) => setForm({ ...form, workNumber: e.target.value })}
            />
          </HalfField>
        </FormRow>

        <FormRow>
          <HalfField label="Email" required htmlFor="my_email">
            <input
              id="my_email"
              placeholder="Email"
              className="form-control"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            {hint('email')}
          </HalfField>
          <HalfField label="Company" required htmlFor="my_company">
            <select
              id="my_company"
              className="form-control"
              required
              value={String(form.companyId)}
              onChange={(e) => setForm({ ...form, companyId: Number(e.target.value) })}
            >
              {profile.companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
            {hint('company')}
          </HalfField>
        </FormRow>

        <FormRow>
          <HalfField label="Occupation" htmlFor="my_occupation">
            <input
              id="my_occupation"
              className="form-control"
              type="text"
              value={form.occupation}
              onChange={(e) => setForm({ ...form, occupation: e.target.value })}
            />
          </HalfField>
          {/* `rows={1}`, ca Address din fisa contului: o adresa de o linie sta pe
              aceeasi grila cu campurile de langa ea, si nu inalta randul. */}
          <HalfField label="Address" htmlFor="my_address">
            <textarea
              id="my_address"
              className="form-control"
              rows={1}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </HalfField>
        </FormRow>

        <FormActions>
          <button className="btn btn-success" type="submit" disabled={busy}>
            {busy ? 'Saving...' : 'Save changes'}
          </button>{' '}
          <button
            className="btn btn-default preview-template color-blue"
            type="button"
            onClick={() => setSignature(true)}
          >
            Signature
          </button>
        </FormActions>
      </form>

      <Modal
        id="previewSignatureModal"
        open={signature}
        onClose={() => setSignature(false)}
        title="Signature body"
        labelledBy="previewSignatureModalLabel"
      >
        {profile.signatureTemplateId > 0 ? (
          <textarea id="sign" className="signature-body form-control" rows={12} readOnly value={profile.signatureBody} />
        ) : (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> No signature template is set on your account.
          </div>
        )}
      </Modal>
    </>
  )
}

/** `my_account/personal_avatar.php`. */
function AvatarForm({ profile }: { profile: MyProfile }) {
  const client = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'my_account'] })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!file) {
      toast('Choose a picture first.', 'Error', 'error')
      return
    }
    const form = new FormData()
    form.append('photo', file)
    setBusy(true)
    void saveMyAvatar(form)
      .then(() => {
        toast('Picture saved', 'Success')
        setFile(null)
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  const drop = () => {
    if (!confirm('Remove your avatar?')) return
    setBusy(true)
    void removeMyAvatar()
      .then(() => {
        toast('Photo removed successfully.', 'Success')
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <form role="form" id="change_avatar_form" method="post" onSubmit={submit}>
      {/* Poza sta in coloana din stanga, exact acolo unde incep etichetele
          celorlalte panouri; alesul fisierului e un camp ca oricare altul. */}
      {profile.user.avatarUrl !== '' ? (
        <FormRow>
          <div className="col-md-6">
            <img
              className="img-thumbnail"
              style={{ width: 140, height: 140 }}
              src={profile.user.avatarUrl}
              alt=""
              onError={(event) => {
                event.currentTarget.style.display = 'none'
              }}
            />
          </div>
        </FormRow>
      ) : null}

      <FormRow>
        <HalfField label="Upload Picture" htmlFor="my_avatar_file">
          {/* Fara `form-control`: pe o casuta de fisier inaltimea fixa de 34px
              taie butonul nativ al browserului. Coloana da alinierea. */}
          <input
            id="my_avatar_file"
            type="file"
            accept="image/png,image/jpeg,image/gif"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </HalfField>
      </FormRow>
      <FormActions>
        <button className="btn btn-success" type="submit" disabled={busy}>
          Save Changes
        </button>{' '}
        <button className="btn btn-danger remove-photo" type="button" onClick={drop} disabled={busy}>
          Remove Avatar
        </button>
      </FormActions>
    </form>
  )
}

/** `my_account/change_password.php`. */
function PasswordForm({ hint }: { hint: string }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setErrors({})
    void changeMyPassword({ current_password: current, new_password: next, confirm_password: confirm })
      .then(() => {
        toast('Password changed', 'Success')
        setCurrent('')
        setNext('')
        setConfirm('')
      })
      .catch((error: unknown) => {
        const fields = error instanceof ApiError ? (error.details?.fields as Record<string, string> | undefined) : undefined
        setErrors(fields ?? {})
        toast(failed(error), 'Error', 'error')
      })
      .finally(() => setBusy(false))
  }

  const bad = (name: string) =>
    errors[name] ? <span className="help-block text-danger">{errors[name]}</span> : null

  return (
    <form role="form" id="change_password_form" method="post" onSubmit={submit}>
      {/* Parola de acum sta singura pe randul ei: e cea pe care o STII, iar
          perechea de dedesubt e cea pe care o ALEGI. */}
      <FormRow>
        <HalfField label="Current Password" required htmlFor="my_current_password">
          <input
            id="my_current_password"
            className="form-control"
            type="password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
          {bad('current_password')}
        </HalfField>
      </FormRow>
      <FormRow>
        {/* Regula se spune INAINTE, nu ca refuz dupa apasarea butonului. */}
        <HalfField label="New Password" required htmlFor="my_new_password" help={hint}>
          <input
            id="my_new_password"
            className="form-control"
            type="password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          {bad('new_password')}
        </HalfField>
        <HalfField label="Confirm New Password" required htmlFor="my_confirm_password">
          <input
            id="my_confirm_password"
            className="form-control"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {bad('confirm_password')}
        </HalfField>
      </FormRow>
      <FormActions>
        <button className="btn btn-success" type="submit" disabled={busy}>
          Change Password
        </button>
      </FormActions>
    </form>
  )
}

/** `my_account/privacy_settings.php` - un singur camp, fusul orar. */
function PrivacyForm() {
  const client = useQueryClient()
  const page = useQuery(myPrivacyQuery)
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (page.data) setValue(page.data.timezone > 0 ? String(page.data.timezone) : '')
  }, [page.data])

  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data) return <Spinner />

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void saveMyPrivacy(Number(value))
      .then(() => {
        toast('Changes saved', 'Success')
        void client.invalidateQueries({ queryKey: ['settings', 'my_account', 'privacy'] })
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <form role="form" id="privacy_settings_form" method="post" onSubmit={submit}>
      <FormRow>
        <HalfField label="Timezone" htmlFor="my_privacy_timezone">
          <select
            id="my_privacy_timezone"
            className="form-control"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          >
            <option value="">Select your timezone...</option>
            {page.data.timezones.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {zone.name}
              </option>
            ))}
          </select>
        </HalfField>
      </FormRow>
      <FormActions>
        <button className="btn btn-success" type="submit" disabled={busy}>
          Save Changes
        </button>
      </FormActions>
    </form>
  )
}

/** `my_account/account_events.php` - acelasi jurnal, doar al omului logat. */
const EVENT_ORDER_NAMES = ['event_date', 'event_message', 'user']

function MyEvents() {
  const [state, setState] = useTableState('settings-my-account-events', {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => eventColumns(), [])
  const table = useQuery(myEventsQuery(tableRequest(state, EVENT_ORDER_NAMES, 'event_date')))

  return (
    <DataTable
      id="settings-my-account-events"
      columns={columns}
      rows={(table.data?.data ?? []) as unknown as Record<string, unknown>[]}
      total={table.data?.recordsFiltered ?? 0}
      fetching={table.isFetching}
      error={table.error ? failed(table.error) : null}
      onRetry={() => void table.refetch()}
      state={state}
      onState={setState}
      rowId={(row) => `rowd_${Number(row.id)}`}
      emptyHint="Nothing has happened on your account yet."
    />
  )
}
